import { axiosInstance } from "@/plugins/axios";
import type { AccessSnapshot } from "@/type/permissions";

const PERMISSION_METHOD_PREFIX = "/api/method/pet_app.api.permissions.";
const PERMISSION_MANAGER_METHOD_PREFIX = "/api/method/frappe.core.page.permission_manager.permission_manager.";

export interface FrappeRole {
  name: string;
  disabled?: 0 | 1;
  desk_access?: 0 | 1;
  home_page?: string | null;
  restrict_to_domain?: string | null;
}

export interface CreateRolePayload {
  role_name: string;
  desk_access: 0 | 1;
  disabled: 0 | 1;
  home_page?: string | null;
  restrict_to_domain?: string | null;
}

export interface FrappeRoleProfileRole {
  role: string;
}

export interface FrappeRoleProfile {
  name: string;
  role_profile: string;
  roles: FrappeRoleProfileRole[];
  custom_is_front?: 0 | 1;
  creation?: string;
  modified?: string;
}

export interface FrappeUserRoleProfile {
  role_profile: string;
}

export interface FrappeUserRole {
  role: string;
}

export interface FrappeUser {
  name: string;
  email?: string | null;
  full_name?: string | null;
  username?: string | null;
  enabled?: 0 | 1;
  user_type?: string | null;
  user_image?: string | null;
  role_profiles?: FrappeUserRoleProfile[];
  roles?: FrappeUserRole[];
}

export interface FrappeUserPermission {
  name: string;
  user: string;
  allow: string;
  for_value: string;
  apply_to_all_doctypes?: 0 | 1;
  applicable_for?: string | null;
  is_default?: 0 | 1;
  creation?: string;
  modified?: string;
}

export interface CreateUserPermissionPayload {
  user: string;
  allow: string;
  for_value: string;
  apply_to_all_doctypes?: 0 | 1;
  applicable_for?: string | null;
  is_default?: 0 | 1;
}

export interface RoleProfilePayload {
  role_profile: string;
  roles: FrappeRoleProfileRole[];
  // Marks role profiles created/managed from this frontend app.
  custom_is_front?: 0 | 1;
}

export interface FrappeDocTypePermission {
  name?: string;
  parent?: string;
  role: string;
  permlevel?: number;
  select?: 0 | 1;
  read?: 0 | 1;
  write?: 0 | 1;
  create?: 0 | 1;
  delete?: 0 | 1;
  submit?: 0 | 1;
  cancel?: 0 | 1;
  amend?: 0 | 1;
  report?: 0 | 1;
  import?: 0 | 1;
  export?: 0 | 1;
  print?: 0 | 1;
  email?: 0 | 1;
  share?: 0 | 1;
  if_owner?: 0 | 1;
}

export interface PermissionManagerOption {
  label: string;
  value: string;
}

export interface PermissionManagerOptions {
  tables: PermissionManagerOption[];
  roles: PermissionManagerOption[];
  permissionTypeMap: Record<string, string[]>;
}

/**
 * The sync payload carries page IDENTITY and metadata only.
 *
 * There is deliberately NO `fallbackRoles` here. Sending it let the backend seed
 * `roles_json` on newly created rows from the frontend's hardcoded role lists,
 * making this app an implicit author of backend permissions. Roles are set by an
 * admin via `UpdatePageAccessSettings`, and by nothing else.
 */
export interface FrontendPageAccessPayload {
  key: string;
  label: string;
  moduleKey?: string;
  doctype?: string;
  paths?: string[];
  pathPrefixes?: string[];
  routeNames?: string[];
}

export interface PageAccessSetting {
  page_key: string;
  label: string;
  module_key?: string | null;
  doctype?: string | null;
  paths: string[];
  path_prefixes: string[];
  route_names: string[];
  enabled: 0 | 1;
  roles: string[];
}

export interface PageAccessUpdatePayload {
  page_key: string;
  enabled: 0 | 1;
  roles: string[];
}

export type PermissionManagerPermissionKey =
  | "select"
  | "read"
  | "write"
  | "create"
  | "delete"
  | "submit"
  | "cancel"
  | "amend"
  | "report"
  | "import"
  | "export"
  | "print"
  | "email"
  | "share"
  | "if_owner";

function resourceUrl(doctype: string, name?: string) {
  const base = `/api/resource/${encodeURIComponent(doctype)}`;

  return name ? `${base}/${encodeURIComponent(name)}` : base;
}

function methodFormBody(args: Record<string, string | number | null | undefined>) {
  const body = new URLSearchParams();

  Object.entries(args).forEach(([key, value]) => {
    if (value === null || value === undefined)
      return;

    body.set(key, String(value));
  });

  return body;
}

const methodFormConfig = {
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
};

function unwrapFrappeMessage<T>(payload: any): T | null {
  const value = payload?.message ?? payload?.data ?? payload;

  return value ?? null;
}

function normalizeStringArray(value: unknown): string[] {
  let source = value;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed)
      return [];

    try {
      source = JSON.parse(trimmed);
    } catch {
      source = trimmed.split(",");
    }
  }

  if (!Array.isArray(source))
    return [];

  return [
    ...new Set(
      source
        .map((item: any) => {
          if (typeof item === "string")
            return item.trim();

          return String(
            item?.role
            || item?.name
            || item?.role_profile
            || item?.roleProfile
            || item?.role_profile_name
            || "",
          ).trim();
        })
        .filter(Boolean),
    ),
  ];
}

function normalizePageAccessSetting(row: any): PageAccessSetting {
  const paths = normalizeStringArray(row?.paths ?? row?.path ?? row?.paths_json);
  const pathPrefixes = normalizeStringArray(row?.path_prefixes ?? row?.pathPrefixes ?? row?.path_prefixes_json);
  const routeNames = normalizeStringArray(row?.route_names ?? row?.routeNames ?? row?.route_names_json);
  const roles = normalizeStringArray(row?.roles ?? row?.roles_json);

  return {
    page_key: String(row?.page_key ?? row?.key ?? "").trim(),
    label: String(row?.label ?? row?.page_key ?? row?.key ?? "").trim(),
    module_key: row?.module_key ?? row?.moduleKey ?? null,
    doctype: row?.doctype ?? null,
    paths,
    path_prefixes: pathPrefixes,
    route_names: routeNames,
    enabled: Number(row?.enabled ?? 0) === 1 ? 1 : 0,
    roles,
  };
}

function normalizePageAccessSettings(payload: any): PageAccessSetting[] {
  const value = unwrapFrappeMessage<any>(payload);
  // Envelope (#16) wraps raw lists as `data.items` / `data.result`.
  const rows: any[] = Array.isArray(value?.pages)
    ? value.pages
    : Array.isArray(value)
      ? value
      : Array.isArray(value?.items)
        ? value.items
        : Array.isArray(value?.result)
          ? value.result
          : [];

  return rows
    .map(normalizePageAccessSetting)
    .filter(row => row.page_key);
}

export function normalizeAccessSnapshot(payload: any): AccessSnapshot | null {
  const value = unwrapFrappeMessage<any>(payload);
  if (!value)
    return null;

  const roles = normalizeStringArray(value.roles ?? value.message?.roles);

  return {
    roles,
    roleProfile: value.roleProfile ?? value.role_profile ?? value.role_profile_name ?? null,
    modules: normalizeStringArray(value.modules),
    pages: normalizeStringArray(value.pages),
    actions: normalizeStringArray(value.actions),
    doctypes: value.doctypes ?? {},
    restrictions: value.restrictions ?? {},
    source: "backend",
    loadedAt: value.loadedAt ?? value.loaded_at ?? new Date().toISOString(),
  };
}

/**
 * "Is the backend app absent?" — classified by failure KIND, never by which
 * module the failure mentions.
 *
 * This previously ended with `|| /pet_app\.api\.permissions/i.test(message)`,
 * which was the single most damaging line in the access system. Frappe echoes
 * the method path in EVERY error about that method, including a permission
 * denial, so a `403 PermissionError` whose `_server_messages` read
 * "…Function pet_app.api.permissions.get_current_access is not whitelisted."
 * matched, and `GetCurrentAccess` returned `null` instead of throwing. The
 * caller saw no failure at all: no redirect for a dead session, and for a
 * logged-in user a silent downgrade to the roles/denied snapshot behind a bare
 * `console.info`.
 *
 * An auth outcome is never evidence about deployment, so 401/403 short-circuit
 * to `false` before any message matching. That also stops a future message that
 * happens to contain "Not Found" from being misread.
 */
function isBackendMethodMissing(error: any) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const message = String(data?.exception || data?.message || data?._server_messages || error?.message || "");

  // The backend answered and refused us. That is an answer, not an absence.
  if (status === 401 || status === 403)
    return false;

  return status === 404
    || /Not Found/i.test(message)
    || /ModuleNotFoundError/i.test(message)
    || /AttributeError/i.test(message);
}

/**
 * One in-flight request, shared.
 *
 * ⚠️ NOT A CACHE — released the instant it settles, so the next call goes to the
 * server and a revoked role is still noticed immediately. The 8-hour snapshot TTL
 * in `permissionHelper` remains the only thing that reuses an ANSWER.
 *
 * It exists because of a measured duplicate: `main.ts` fires `GetPermission()`
 * right after mount while the router guard awaits `ensureAccessLoadedForRouter()`
 * for the same navigation, so **every page load asked the server who the user was
 * twice** — on the critical path, before anything renders. Neither caller is
 * wrong and neither knows about the other, so the fix belongs here.
 */
let currentAccessInFlight: Promise<AccessSnapshot | null> | null = null;

export async function GetCurrentAccess(): Promise<AccessSnapshot | null> {
  if (currentAccessInFlight)
    return currentAccessInFlight;

  currentAccessInFlight = (async () => {
    try {
      const response = await axiosInstance.get(`${PERMISSION_METHOD_PREFIX}get_current_access`);

      return normalizeAccessSnapshot(response.data);
    } catch (error: any) {
      if (isBackendMethodMissing(error)) {
        console.info("Dynamic access snapshot backend is not installed yet.");

        return null;
      }

      throw error;
    }
  })().finally(() => {
    // ⚠️ Cleared on BOTH paths. A failure left here would pin every later call to
    // the same rejection, and a 401 during boot would then look permanent.
    currentAccessInFlight = null;
  });

  return currentAccessInFlight;
}

export async function GetPageAccessSettings(): Promise<PageAccessSetting[]> {
  try {
    const response = await axiosInstance.get(`${PERMISSION_METHOD_PREFIX}get_page_access_settings`);

    return normalizePageAccessSettings(response.data);
  } catch (error: any) {
    if (isBackendMethodMissing(error)) {
      console.info("Page access settings backend is not installed yet.");

      return [];
    }

    throw error;
  }
}

export async function SyncFrontendPages(
  pages: FrontendPageAccessPayload[],
): Promise<PageAccessSetting[]> {
  const response = await axiosInstance.post(`${PERMISSION_METHOD_PREFIX}sync_frontend_pages`, {
    pages,
  });

  return normalizePageAccessSettings(response.data);
}

export async function UpdatePageAccessSettings(
  pages: PageAccessUpdatePayload[],
): Promise<PageAccessSetting[]> {
  const response = await axiosInstance.post(`${PERMISSION_METHOD_PREFIX}update_page_access_settings`, {
    pages,
  });

  return normalizePageAccessSettings(response.data);
}

export async function GetRoles(): Promise<FrappeRole[]> {
  const response = await axiosInstance.get(resourceUrl("Role"), {
    params: {
      fields: JSON.stringify(["name", "disabled", "desk_access", "home_page", "restrict_to_domain"]),
      limit_page_length: 999,
      order_by: "name asc",
    },
  });

  return response.data?.data ?? [];
}

export async function CreateRole(payload: CreateRolePayload): Promise<FrappeRole> {
  const response = await axiosInstance.post(resourceUrl("Role"), payload);

  return response.data?.data;
}

export async function GetRoleProfiles(): Promise<FrappeRoleProfile[]> {
  const response = await axiosInstance.get(resourceUrl("Role Profile"), {
    params: {
      fields: JSON.stringify(["name", "role_profile", "creation", "modified"]),
      limit_page_length: 999,
      order_by: "modified desc",
    },
  });

  const summaries: FrappeRoleProfile[] = response.data?.data ?? [];

  return Promise.all(summaries.map(profile => GetRoleProfile(profile.name)));
}

export async function GetRoleProfile(name: string): Promise<FrappeRoleProfile> {
  const response = await axiosInstance.get(resourceUrl("Role Profile", name));
  const profile = response.data?.data ?? {};

  return {
    ...profile,
    role_profile: profile.role_profile ?? profile.name ?? name,
    roles: Array.isArray(profile.roles) ? profile.roles : [],
  };
}

export async function CreateRoleProfile(payload: RoleProfilePayload): Promise<FrappeRoleProfile> {
  const response = await axiosInstance.post(resourceUrl("Role Profile"), payload);

  return response.data?.data;
}

export async function UpdateRoleProfile(name: string, payload: RoleProfilePayload): Promise<FrappeRoleProfile> {
  const response = await axiosInstance.put(resourceUrl("Role Profile", name), payload);

  return response.data?.data;
}

export async function DeleteRoleProfile(name: string): Promise<void> {
  await axiosInstance.delete(resourceUrl("Role Profile", name));
}

export async function GetUsers(): Promise<FrappeUser[]> {
  const response = await axiosInstance.get(resourceUrl("User"), {
    params: {
      fields: JSON.stringify(["name", "email", "full_name", "username", "enabled", "user_type", "user_image"]),
      // Same set as the System Users page. Not `user_type != "Website User"`: the backend
      // demotes any user whose roles have no desk access to Website User (new staff, custom-role
      // profiles, drivers), and that filter hid exactly the people who still needed access.
      filters: JSON.stringify([
        ["User", "name", "!=", "Guest"],
        ["User", "name", "not like", "%@petapp.local"],
      ]),
      limit_page_length: 999,
      order_by: "full_name asc",
    },
  });

  const users: FrappeUser[] = response.data?.data ?? [];

  return Promise.all(users.map(user => GetUser(user.name)));
}

export async function GetUser(name: string): Promise<FrappeUser> {
  const response = await axiosInstance.get(resourceUrl("User", name));
  const user = response.data?.data ?? {};

  return {
    ...user,
    role_profiles: Array.isArray(user.role_profiles) ? user.role_profiles : [],
    roles: Array.isArray(user.roles) ? user.roles : [],
  };
}

export async function UpdateUserRoleProfiles(
  user: string,
  roleProfiles: FrappeUserRoleProfile[],
): Promise<FrappeUser> {
  return UpdateUserRoleAssignments(user, roleProfiles, undefined);
}

export async function UpdateUserRoleAssignments(
  user: string,
  roleProfiles: FrappeUserRoleProfile[],
  roles?: FrappeUserRole[],
): Promise<FrappeUser> {
  const response = await axiosInstance.put(resourceUrl("User", user), {
    role_profiles: roleProfiles,
    ...(roles ? { roles } : {}),
  });

  return response.data?.data;
}

export async function GetUserPermissions(): Promise<FrappeUserPermission[]> {
  const response = await axiosInstance.get(resourceUrl("User Permission"), {
    params: {
      fields: JSON.stringify([
        "name",
        "user",
        "allow",
        "for_value",
        "apply_to_all_doctypes",
        "applicable_for",
        "is_default",
        "creation",
        "modified",
      ]),
      limit_page_length: 999,
      order_by: "modified desc",
    },
  });

  return response.data?.data ?? [];
}

export async function CreateUserPermission(
  payload: CreateUserPermissionPayload,
): Promise<FrappeUserPermission> {
  const response = await axiosInstance.post(resourceUrl("User Permission"), payload);

  return response.data?.data;
}

export async function DeleteUserPermission(name: string): Promise<void> {
  await axiosInstance.delete(resourceUrl("User Permission", name));
}

export async function GetDocTypePermissions(doctype: string): Promise<FrappeDocTypePermission[]> {
  const response = await axiosInstance.get(resourceUrl("DocType", doctype));
  const doc = response.data?.data ?? {};

  return Array.isArray(doc.permissions) ? doc.permissions : [];
}

export async function GetPermissionManagerOptions(): Promise<PermissionManagerOptions> {
  const response = await axiosInstance.get(`${PERMISSION_MANAGER_METHOD_PREFIX}get_roles_and_doctypes`);
  const message = response.data?.message ?? {};

  return {
    tables: message.doctypes ?? [],
    roles: message.roles ?? [],
    permissionTypeMap: message.doctype_ptype_map ?? {},
  };
}

export async function GetPermissionManagerPermissions(
  table: string,
  role?: string | null,
): Promise<FrappeDocTypePermission[]> {
  const response = await axiosInstance.get(`${PERMISSION_MANAGER_METHOD_PREFIX}get_permissions`, {
    params: {
      doctype: table,
      ...(role ? { role } : {}),
    },
  });

  return response.data?.message ?? [];
}

export async function AddPermissionManagerRule(
  table: string,
  role: string,
  permlevel: number,
): Promise<void> {
  await axiosInstance.post(
    `${PERMISSION_MANAGER_METHOD_PREFIX}add`,
    methodFormBody({
      parent: table,
      role,
      permlevel,
    }),
    methodFormConfig,
  );
}

export async function UpdatePermissionManagerRule(
  table: string,
  role: string,
  permlevel: number,
  permission: PermissionManagerPermissionKey,
  value: 0 | 1,
  ifOwner: 0 | 1 = 0,
): Promise<string | null> {
  const response = await axiosInstance.post(
    `${PERMISSION_MANAGER_METHOD_PREFIX}update`,
    methodFormBody({
      doctype: table,
      role,
      permlevel,
      ptype: permission,
      value,
      if_owner: ifOwner,
    }),
    methodFormConfig,
  );

  return response.data?.message ?? null;
}

export async function RemovePermissionManagerRule(
  table: string,
  role: string,
  permlevel: number,
  ifOwner: 0 | 1 = 0,
): Promise<void> {
  await axiosInstance.post(
    `${PERMISSION_MANAGER_METHOD_PREFIX}remove`,
    methodFormBody({
      doctype: table,
      role,
      permlevel,
      if_owner: ifOwner,
    }),
    methodFormConfig,
  );
}

export async function ResetPermissionManagerRules(table: string): Promise<void> {
  await axiosInstance.post(
    `${PERMISSION_MANAGER_METHOD_PREFIX}reset`,
    methodFormBody({
      doctype: table,
    }),
    methodFormConfig,
  );
}
